import React, { useState, useEffect } from 'react';
import './App.css';
import MedicineList from './components/MedicineList';
import SearchBar from './components/SearchBar';
import FilterPanel from './components/FilterPanel';
import Statistics from './components/Statistics';
import Header from './components/Header';
import Navigation from './components/Navigation';
import OfflineIndicator from './components/OfflineIndicator';
import POS from './pages/POS';
import SalesHistory from './pages/SalesHistory';
import StockManagement from './pages/StockManagement';
import PurchaseOrders from './pages/PurchaseOrders';
import Profile from './pages/Profile';
import Returns from './pages/Returns';
import ProfitTracking from './pages/ProfitTracking';
import AdminLogin from './pages/Admin/AdminLogin';
import AdminDashboard from './pages/Admin/AdminDashboard';
import PharmacyLogin from './pages/PharmacyLogin';
import ElectronPOS from './pages/ElectronPOS';
import API_BASE_URL from './config/api';
// Data sync disabled for web version - only used in Electron
// import { initDatabase, downloadAllData, checkAndSync, getSyncQueueStatus } from './services/dataSync';

function App() {
  // ALL HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS
  // Check if running in Electron mode (check after component mounts to avoid hook issues)
  const [isElectron, setIsElectron] = React.useState(false);
  
  const [currentPage, setCurrentPage] = useState('browse');
  const [admin, setAdmin] = useState(null);
  const [adminToken, setAdminToken] = useState(null);
  const [pharmacyUser, setPharmacyUser] = useState(null);
  const [pharmacyToken, setPharmacyToken] = useState(null);
  const [medicines, setMedicines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    manufacturer: '',
    minPrice: '',
    maxPrice: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [categories, setCategories] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // Check for Electron mode
  React.useEffect(() => {
    // Check for Electron mode
    const electronMode = window.location.hash === '#electron' || 
                         (typeof window !== 'undefined' && window.electronAPI);
    setIsElectron(electronMode);
  }, []);

  // Data sync initialization disabled for web version
  // (Only used in Electron for offline functionality)

  // Check for admin or pharmacy session on mount and download data
  useEffect(() => {
    let isMounted = true;
    
    const savedAdmin = localStorage.getItem('adminUser');
    const savedAdminToken = localStorage.getItem('adminToken');
    const savedPharmacyUser = localStorage.getItem('pharmacyUser');
    const savedPharmacyToken = localStorage.getItem('pharmacyToken');
    
    if (savedAdmin && savedAdminToken) {
      setAdmin(JSON.parse(savedAdmin));
      setAdminToken(savedAdminToken);
    }
    
    if (savedPharmacyUser && savedPharmacyToken) {
      setPharmacyUser(JSON.parse(savedPharmacyUser));
      setPharmacyToken(savedPharmacyToken);
      
      // Data download disabled for web version
      // (Only used in Electron for offline functionality)
    }
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Online/offline event listeners (data sync disabled for web)
  useEffect(() => {
    const handleOnline = () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[App] Connection restored');
      }
    };

    const handleOffline = () => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[App] Connection lost - working offline');
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Service worker message listener (minimal, no data sync)
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        // Service worker messages handled but no data sync for web
        if (process.env.NODE_ENV === 'development' && event.data) {
          console.log('[App] Service worker message:', event.data.type);
        }
      });
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [pharmacyToken]);

  useEffect(() => {
    if (currentPage === 'browse' && !isElectron) {
      fetchMedicines();
      fetchCategories();
      fetchManufacturers();
    }
  }, [filters, pagination.page, currentPage, isElectron]);

  // Show simplified Electron POS if in Electron mode (AFTER all hooks)
  if (isElectron) {
    return <ElectronPOS />;
  }

  const fetchMedicines = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ''))
      });

      const response = await fetch(`${API_BASE_URL}/api/medicines?${params}`);
      if (!response.ok) throw new Error('Failed to fetch medicines');
      
      const data = await response.json();
      setMedicines(data.medicines);
      setPagination(prev => ({
        ...prev,
        total: data.total,
        totalPages: data.totalPages
      }));
      setError(null);
    } catch (err) {
      setError(err.message);
      setMedicines([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/categories`);
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchManufacturers = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/manufacturers`);
      if (response.ok) {
        const data = await response.json();
        setManufacturers(data);
      }
    } catch (err) {
      console.error('Error fetching manufacturers:', err);
    }
  };

  const handleNavigate = (page) => {
    setCurrentPage(page);
  };

  const handleAdminLogin = (adminData, token) => {
    setAdmin(adminData);
    setAdminToken(token);
    setCurrentPage('admin-dashboard');
  };

  const handleAdminLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    setAdmin(null);
    setAdminToken(null);
    setCurrentPage('browse');
  };

  const handlePharmacyLogin = (userData, token) => {
    setPharmacyUser(userData);
    setPharmacyToken(token);
    setCurrentPage('pos'); // Redirect to POS after login
  };

  const handlePharmacyLogout = () => {
    localStorage.removeItem('pharmacyToken');
    localStorage.removeItem('pharmacyUser');
    setPharmacyUser(null);
    setPharmacyToken(null);
    setCurrentPage('browse');
  };

  // Pharmacy login route
  if (currentPage === 'pharmacy-login') {
    return <PharmacyLogin onLogin={handlePharmacyLogin} />;
  }

  // Admin routes
  if (!admin && (currentPage === 'admin' || currentPage === 'admin-dashboard')) {
    return <AdminLogin onLogin={handleAdminLogin} />;
  }

  if (admin && currentPage === 'admin-dashboard') {
    return <AdminDashboard admin={admin} token={adminToken} onLogout={handleAdminLogout} />;
  }

  const handleSearchChange = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      category: '',
      manufacturer: '',
      minPrice: '',
      maxPrice: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  if (currentPage === 'pos') {
    // Check if pharmacy user is logged in
    if (!pharmacyUser) {
      return <PharmacyLogin onLogin={handlePharmacyLogin} />;
    }
    return <POS onNavigate={handleNavigate} user={pharmacyUser} token={pharmacyToken} onLogout={handlePharmacyLogout} />;
  }

  if (currentPage === 'sales') {
    if (!pharmacyUser) {
      return <PharmacyLogin onLogin={handlePharmacyLogin} />;
    }
    return <SalesHistory onNavigate={handleNavigate} user={pharmacyUser} token={pharmacyToken} />;
  }

  if (currentPage === 'stock') {
    if (!pharmacyUser) {
      return <PharmacyLogin onLogin={handlePharmacyLogin} />;
    }
    return <StockManagement onNavigate={handleNavigate} user={pharmacyUser} token={pharmacyToken} />;
  }

  if (currentPage === 'purchase-orders') {
    if (!pharmacyUser) {
      return <PharmacyLogin onLogin={handlePharmacyLogin} />;
    }
    return <PurchaseOrders onNavigate={handleNavigate} user={pharmacyUser} token={pharmacyToken} />;
  }

  if (currentPage === 'profile') {
    if (!pharmacyUser) {
      return <PharmacyLogin onLogin={handlePharmacyLogin} />;
    }
    return <Profile onNavigate={handleNavigate} user={pharmacyUser} token={pharmacyToken} />;
  }

  if (currentPage === 'returns') {
    if (!pharmacyUser) {
      return <PharmacyLogin onLogin={handlePharmacyLogin} />;
    }
    return <Returns token={pharmacyToken} user={pharmacyUser} onBack={() => handleNavigate('pos')} />;
  }

  if (currentPage === 'profit') {
    if (!pharmacyUser) {
      return <PharmacyLogin onLogin={handlePharmacyLogin} />;
    }
    return <ProfitTracking token={pharmacyToken} user={pharmacyUser} onBack={() => handleNavigate('pos')} onLogout={handlePharmacyLogout} />;
  }

  if (currentPage === 'admin') {
    return <AdminLogin onLogin={handleAdminLogin} />;
  }

  return (
    <div className="App">
      <OfflineIndicator />
      <Navigation currentPage={currentPage} onNavigate={handleNavigate} />
      {admin && (
        <div className="admin-badge">
          <span>Admin Mode</span>
          <button onClick={() => setCurrentPage('admin-dashboard')}>Go to Admin Panel</button>
        </div>
      )}
      {pharmacyUser && (
        <div className="pharmacy-badge">
          <span>🏥 {pharmacyUser.pharmacyName || pharmacyUser.username}</span>
          <button onClick={handlePharmacyLogout}>Logout</button>
        </div>
      )}
      <Header />
      <div className="container">
        <Statistics />
        <div className="search-section">
          <SearchBar 
            value={filters.search}
            onChange={handleSearchChange}
            onToggleFilters={() => setShowFilters(!showFilters)}
            showFilters={showFilters}
          />
        </div>
        
        {showFilters && (
          <FilterPanel
            filters={filters}
            categories={categories}
            manufacturers={manufacturers}
            onChange={handleFilterChange}
            onClear={clearFilters}
          />
        )}

        {error && (
          <div className="error-message">
            <p>Error: {error}</p>
          </div>
        )}

        <MedicineList 
          medicines={medicines}
          loading={loading}
        />

        {pagination.totalPages > 1 && (
          <div className="pagination">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="pagination-btn"
            >
              Previous
            </button>
            <span className="pagination-info">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
            </span>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === pagination.totalPages}
              className="pagination-btn"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
